import { useState } from 'react'
import {
  Button, CloseButton, Drawer, HStack, Portal, Text, Textarea, VStack,
} from '@chakra-ui/react'
import type { AskResponse } from '../../types/lobbyDay'

export interface AttendeeOutcomeInput {
  response: AskResponse
  notes: string
}

export interface AttendeeOutcomeSheetProps {
  isOpen: boolean
  onClose: () => void
  /** Fires when the attendee submits a response and no terms acknowledgement is outstanding. */
  onSubmit: (input: AttendeeOutcomeInput) => void
  /** Fires from the "I agree" action, re-submitting the same input with terms accepted. */
  onAcceptTerms: (input: AttendeeOutcomeInput) => void
  /** True when the credential seam's redeem call reported the [LEGAL] terms ack is outstanding. */
  termsRequired: boolean
  /** True when redeem 404'd -- a pre-reseed attendee link with no paired grant yet. */
  unavailable: boolean
  isSubmitting?: boolean
  /** True once the attendee has recorded an outcome — flips the header + save copy to edit mode. */
  hasExistingOutcome?: boolean
}

const RESPONSES: { value: AskResponse; label: string; palette: string }[] = [
  { value: 'Yes', label: 'Yes', palette: 'green' },
  { value: 'No', label: 'No', palette: 'red' },
  { value: 'Maybe', label: 'Maybe', palette: 'yellow' },
]

export function AttendeeOutcomeSheet({
  isOpen, onClose, onSubmit, onAcceptTerms, termsRequired, unavailable, isSubmitting, hasExistingOutcome,
}: AttendeeOutcomeSheetProps) {
  // Local selection -- deliberately NOT reset when `termsRequired` flips true: the attendee
  // already picked a response before the mutation reported terms were outstanding, and "I
  // agree" needs to re-submit that same selection, not a blank one.
  const [response, setResponse] = useState<AskResponse | null>(null)
  const [notes, setNotes] = useState('')

  const currentInput = (): AttendeeOutcomeInput => ({ response: response ?? 'NotRecorded', notes })

  return (
    <Drawer.Root open={isOpen} placement="bottom" onOpenChange={(e) => { if (!e.open) onClose() }}>
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content borderTopRadius="xl" maxH="85vh" pb="env(safe-area-inset-bottom, 0px)" bg="surface">
            <Drawer.CloseTrigger asChild><CloseButton size="sm" /></Drawer.CloseTrigger>
            <Drawer.Header borderBottomWidth="1px" borderColor="border" color="ink">
              {hasExistingOutcome ? 'Edit outcome' : 'Record outcome'}
            </Drawer.Header>
            <Drawer.Body py={4}>
              {unavailable ? (
                <Text color="ink2">
                  Recording isn't available for this link yet — check with your organizer.
                </Text>
              ) : termsRequired ? (
                <VStack align="stretch" gap={4}>
                  <Text color="ink">
                    You're recording your own notes on this meeting. This information is used
                    only for this lobby day's advocacy and is visible to your organizers.
                  </Text>
                  <Button
                    colorPalette="green"
                    minH="44px"
                    loading={isSubmitting}
                    onClick={() => onAcceptTerms(currentInput())}
                  >
                    I agree
                  </Button>
                </VStack>
              ) : (
                <VStack align="stretch" gap={4}>
                  <HStack gap={2}>
                    {RESPONSES.map((r) => (
                      <Button
                        key={r.value}
                        flex="1"
                        minH="44px"
                        colorPalette={r.palette}
                        variant={response === r.value ? 'solid' : 'outline'}
                        onClick={() => setResponse(r.value)}
                      >
                        {r.label}
                      </Button>
                    ))}
                  </HStack>
                  <Textarea
                    placeholder="Notes (optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    borderColor="border"
                    color="ink"
                  />
                  <Button
                    colorPalette="brand"
                    minH="44px"
                    disabled={!response}
                    loading={isSubmitting}
                    onClick={() => onSubmit(currentInput())}
                  >
                    {hasExistingOutcome ? 'Update response' : 'Save response'}
                  </Button>
                </VStack>
              )}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}
